import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { Box, Flex, Text, VStack, Icon } from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { HiHome, HiPhotograph } from "react-icons/hi";
import { AiOutlineFire } from "react-icons/ai";
import { BiCalendar } from "react-icons/bi";
import { MdRestaurantMenu } from "react-icons/md";
import { FaUser } from "react-icons/fa";
import { HiPlusCircle } from "react-icons/hi";
const NAV_WIDTH = 400;
const BAR_HEIGHT = 68;
const BAR_TOP = 36;
const BAR_RADIUS = 32;
const BAR_BORDER = 0.5;
const CENTER_BTN = 68;
const CENTER_TOP = 10;
const CENTER_GAP = 118;
const NOTCH_EXTRA = 24;
const NOTCH_START = (NAV_WIDTH - CENTER_GAP) / 2 - NOTCH_EXTRA;
const NOTCH_END = (NAV_WIDTH + CENTER_GAP) / 2 + NOTCH_EXTRA;
const NOTCH_DEPTH = 47;
const NOTCH_SHOULDER_OUTER = 28;
const NOTCH_SHOULDER_INNER = 42;
const NOTCH_CONFIG_DEPTH = NOTCH_DEPTH;
const NOTCH_CONFIG_RADIUS = NOTCH_SHOULDER_INNER;
const COMPONENT_HEIGHT = BAR_TOP + BAR_HEIGHT;
const toggleIconPop = keyframes `
  0% {
    transform: translateY(6px) scale(0.7) rotate(-14deg);
    opacity: 0;
    filter: drop-shadow(0 0 0 rgba(129, 85, 254, 0));
  }
  50% {
    transform: translateY(-2px) scale(1.12) rotate(4deg);
    opacity: 1;
    filter: drop-shadow(0 0 12px rgba(129, 85, 254, 0.55));
  }
  100% {
    transform: translateY(0) scale(1) rotate(0deg);
    opacity: 1;
    filter: drop-shadow(0 0 0 rgba(129, 85, 254, 0));
  }
`;
const toggleLabelSlide = keyframes `
  0% {
    transform: translateY(4px);
    opacity: 0;
    letter-spacing: 0.08em;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
    letter-spacing: 0.01em;
  }
`;
export const BottomNavBar = ({ onNavigate, activeId, showMenu = false, showEvents = false, labels, menuColors, }) => {
    const [offersToggleTick, setOffersToggleTick] = useState(0);
    const [menuToggleTick, setMenuToggleTick] = useState(0);
    const containerRef = useRef(null);
    const [renderedWidth, setRenderedWidth] = useState(NAV_WIDTH);
    useEffect(() => {
        const el = containerRef.current;
        if (!el)
            return;
        const update = () => setRenderedWidth(el.getBoundingClientRect().width);
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    useEffect(() => {
        setOffersToggleTick((prev) => prev + 1);
    }, [showEvents]);
    useEffect(() => {
        setMenuToggleTick((prev) => prev + 1);
    }, [showMenu]);
    const activeColor = (menuColors === null || menuColors === void 0 ? void 0 : menuColors.primary) || "#8155FE";
    const textColor = (menuColors === null || menuColors === void 0 ? void 0 : menuColors.text) || "#797979";
    const iconColor = (menuColors === null || menuColors === void 0 ? void 0 : menuColors.text) || "#797979";
    const navBackground = (menuColors === null || menuColors === void 0 ? void 0 : menuColors.bg) || "#232323";
    const navBorder = "#5B5959";
    const reserveButtonBg = activeColor;
    const reserveButtonIconColor = "white";
    const reserveButtonTextColor = "rgba(255,255,255,0.96)";
    const resolvedNotchDepth = Math.min(BAR_HEIGHT - 8, Math.max(8, NOTCH_CONFIG_DEPTH));
    const resolvedNotchRadius = Math.min(CENTER_GAP / 2 - 2, Math.max(8, NOTCH_CONFIG_RADIUS));
    const resolvedNotchShoulderOuter = Math.max(NOTCH_SHOULDER_OUTER, resolvedNotchRadius + 14);
    const resolvedLabels = {
        home: "Home",
        offers: "Offers",
        events: "Events",
        menu: "Menu",
        gallery: "Gallery",
        profile: "Profile",
        reserve: "Reserve",
        ...labels,
    };
    const menuOrGalleryId = showMenu ? "menu" : "gallery";
    const leftItems = [
        {
            id: "home",
            label: resolvedLabels.home,
            icon: _jsx(Icon, { as: HiHome }),
            onClick: onNavigate.home,
        },
        {
            id: "offers",
            label: showEvents ? resolvedLabels.events : resolvedLabels.offers,
            icon: _jsx(Icon, { as: showEvents ? BiCalendar : AiOutlineFire }),
            onClick: onNavigate.offers,
        },
    ];
    const rightItems = [
        {
            id: menuOrGalleryId,
            label: showMenu ? resolvedLabels.menu : resolvedLabels.gallery,
            icon: _jsx(Icon, { as: showMenu ? MdRestaurantMenu : HiPhotograph }),
            onClick: onNavigate.menuOrGallery,
        },
        {
            id: "profile",
            label: resolvedLabels.profile,
            icon: _jsx(Icon, { as: FaUser }),
            onClick: onNavigate.profile,
        },
    ];
    const renderNavItem = (item) => {
        const isActive = item.id === activeId;
        const shouldAnimateOffersToggle = item.id === "offers";
        const shouldAnimateMenuToggle = item.id === menuOrGalleryId;
        const animationTick = shouldAnimateOffersToggle
            ? offersToggleTick
            : shouldAnimateMenuToggle
                ? menuToggleTick
                : 0;
        const shouldAnimate = shouldAnimateOffersToggle || shouldAnimateMenuToggle;
        return (_jsxs(VStack, { spacing: "4px", align: "center", justify: "center", flex: 1, cursor: "pointer", onClick: item.onClick, role: "button", "aria-label": item.label, userSelect: "none", transition: "opacity 0.15s", _active: { opacity: 0.65 }, h: "100%", textAlign: "center", children: [_jsx(Box, { fontSize: "24px", lineHeight: "1", color: isActive ? activeColor : iconColor, opacity: isActive ? 1 : 0.9, transition: "color 0.2s", animation: shouldAnimate
                        ? `${toggleIconPop} 460ms cubic-bezier(0.2, 0.9, 0.2, 1)`
                        : undefined, sx: {
                        "& svg": {
                            width: "24px !important",
                            height: "24px !important",
                        },
                    }, children: item.icon }), _jsx(Text, { fontSize: "10px", fontWeight: 400, color: isActive ? activeColor : textColor, opacity: isActive ? 1 : 0.9, lineHeight: "1", letterSpacing: "0.01em", transition: "color 0.2s", animation: shouldAnimate ? `${toggleLabelSlide} 360ms ease-out` : undefined, children: item.label })] }, shouldAnimate ? `${item.id}-${animationTick}` : item.id));
    };
    return (_jsxs(Box, { ref: containerRef, position: "relative", w: `min(${NAV_WIDTH}px, calc(100vw - 16px))`, mx: "auto", h: `${COMPONENT_HEIGHT}px`, overflow: "visible", children: [_jsx(Box, { position: "absolute", top: `${CENTER_TOP}px`, left: "50%", transform: "translateX(-50%)", zIndex: 20, children: _jsxs(Box, { as: "button", w: `${CENTER_BTN}px`, h: `${CENTER_BTN}px`, borderRadius: "full", bg: reserveButtonBg, border: "none", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "2px", color: "white", cursor: "pointer", boxShadow: "0 10px 24px rgba(0,0,0,0.35)", transition: "transform 0.15s ease, box-shadow 0.15s ease", onClick: onNavigate.reserve, "aria-label": resolvedLabels.reserve, _hover: {
                        transform: "translateY(-1px)",
                        boxShadow: "0 14px 28px rgba(0,0,0,0.38)",
                    }, _active: { transform: "scale(0.96)" }, _focus: { outline: "none" }, p: "8px", children: [_jsx(Box, { w: "30px", h: "30px", borderRadius: "full", color: reserveButtonIconColor, display: "flex", alignItems: "center", justifyContent: "center", bg: reserveButtonBg, fontSize: "30px", lineHeight: "1", sx: {
                                "& svg": {
                                    width: "30px !important",
                                    height: "30px !important",
                                },
                            }, children: _jsx(Icon, { as: HiPlusCircle }) }), _jsx(Text, { fontSize: "10px", fontWeight: 400, color: reserveButtonTextColor, lineHeight: "1", textAlign: "center", children: resolvedLabels.reserve })] }) }), _jsxs(Box, { position: "absolute", top: `${BAR_TOP}px`, left: 0, right: 0, h: `${BAR_HEIGHT}px`, zIndex: 10, overflow: "visible", bg: "transparent", children: [(() => {
                        const W = renderedWidth;
                        const H = BAR_HEIGHT;
                        const notchHalf = CENTER_GAP / 2 + NOTCH_EXTRA;
                        const nStart = W / 2 - notchHalf;
                        const nEnd = W / 2 + notchHalf;
                        const inset = BAR_BORDER / 2;
                        const left = inset;
                        const right = W - inset;
                        const top = inset;
                        const bottom = H - inset;
                        const r = BAR_RADIUS;
                        const pathD = `
            M ${left + r} ${bottom}
            H ${right - r}
            A ${r} ${r} 0 0 0 ${right} ${bottom - r}
            V ${top + r}
            A ${r} ${r} 0 0 0 ${right - r} ${top}
            H ${nEnd}
            C ${nEnd - resolvedNotchShoulderOuter} ${top} ${W / 2 + resolvedNotchRadius} ${resolvedNotchDepth} ${W / 2} ${resolvedNotchDepth}
            C ${W / 2 - resolvedNotchRadius} ${resolvedNotchDepth} ${nStart + resolvedNotchShoulderOuter} ${top} ${nStart} ${top}
            H ${left + r}
            A ${r} ${r} 0 0 0 ${left} ${top + r}
            V ${bottom - r}
            A ${r} ${r} 0 0 0 ${left + r} ${bottom}
            Z
          `;
                        const clipPath = `path("${pathD.replace(/\s+/g, " ").trim()}")`;
                        return (_jsxs(_Fragment, { children: [_jsx(Box, { position: "absolute", inset: 0, pointerEvents: "none", sx: {
                                        WebkitBackdropFilter: "blur(18px) saturate(140%)",
                                        backdropFilter: "blur(18px) saturate(140%)",
                                        WebkitClipPath: clipPath,
                                        clipPath: clipPath,
                                    } }), _jsx(Box, { position: "absolute", inset: 0, pointerEvents: "none", children: _jsx("svg", { width: W, height: H, viewBox: `0 0 ${W} ${H}`, xmlns: "http://www.w3.org/2000/svg", style: {
                                            overflow: "visible",
                                            display: "block",
                                            filter: "drop-shadow(0 30px 28px rgba(0,0,0,0.20)) drop-shadow(0 22px 18px rgba(0,0,0,0.30))",
                                        }, shapeRendering: "geometricPrecision", children: _jsx("path", { d: pathD, fill: navBackground, fillOpacity: 0.8, stroke: navBorder, strokeWidth: BAR_BORDER }) }) })] }));
                    })(), _jsxs(Flex, { position: "absolute", inset: 0, align: "center", h: "100%", children: [_jsx(Flex, { flex: 1, align: "center", justify: "center", h: "100%", pl: 4, gap: 6, children: leftItems.map(renderNavItem) }), _jsx(Box, { w: `${CENTER_GAP}px`, flexShrink: 0 }), _jsx(Flex, { flex: 1, align: "center", justify: "center", h: "100%", pr: 4, gap: 6, children: rightItems.map(renderNavItem) })] })] })] }));
};
export default BottomNavBar;
